import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient } from "../../App";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Paperclip, X } from "lucide-react";
import { CATEGORY, URGENCY, AREAS } from "./helpDesk";

const EMPTY = { title: "", details: "", why: "", category: "feature", urgency: "normal", area: "" };

// Opened from the Help Desk page and from the "Request" button in the top bar,
// so whoever files it can do so from the page they're stuck on — that page's
// path is sent along as page_url.
export default function NewRequestDialog({ open, onOpenChange, pageUrl, onCreated, viewLink = false }) {
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(EMPTY);
      setFiles([]);
    }
  }, [open]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const addFiles = (list) => {
    const picked = Array.from(list || []).filter((f) => f.size <= 15 * 1024 * 1024);
    if (picked.length < (list?.length || 0)) toast.error("Files over 15 MB were skipped");
    setFiles((prev) => [...prev, ...picked].slice(0, 6));
  };

  // Paste a screenshot straight into the form.
  const onPaste = (e) => {
    const images = Array.from(e.clipboardData?.files || []).filter((f) => f.type.startsWith("image/"));
    if (images.length) {
      e.preventDefault();
      addFiles(images.map((f, i) => new File([f], f.name && f.name !== "image.png" ? f.name : `screenshot-${Date.now()}-${i}.png`, { type: f.type })));
    }
  };

  const submit = async () => {
    if (form.title.trim().length < 3 || form.details.trim().length < 3) {
      toast.error("Add a short title and what you need");
      return;
    }
    setSaving(true);
    try {
      const { data } = await apiClient.post("/help-desk/requests", {
        ...form,
        area: form.area || null,
        why: form.why || null,
        page_url: pageUrl || null,
      });
      let failed = 0;
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        try {
          await apiClient.post(`/help-desk/requests/${data.id}/attachments`, fd);
        } catch {
          failed += 1;
        }
      }
      if (failed) toast.error(`${failed} attachment${failed > 1 ? "s" : ""} didn't upload`);
      toast.success(`${data.ref} filed. You'll be notified as it moves.`, viewLink ? {
        action: { label: "View", onClick: () => navigate(`/help-desk?request=${data.id}`) },
      } : undefined);
      onOpenChange(false);
      onCreated?.(data);
    } catch (e) {
      toast.error(e.response?.data?.detail?.toString?.() || "Couldn't file the request");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[560px] max-h-[90vh] overflow-y-auto" onPaste={onPaste} data-testid="help-desk-new-dialog">
        <DialogHeader>
          <DialogTitle className="font-light">New request</DialogTitle>
          <DialogDescription>
            Tell HQ what you need. It goes to the approvers' inbox and you can follow it to done.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="hd-title">Title</Label>
            <Input id="hd-title" value={form.title} onChange={set("title")} maxLength={200}
              placeholder="e.g. Add drone footage as a line on the Client Calculator" data-testid="hd-title" />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="hd-details">What do you need?</Label>
            <Textarea id="hd-details" value={form.details} onChange={set("details")} rows={5}
              placeholder="Describe it like you'd text it. What happened, what you expected, which client or project if any."
              data-testid="hd-details" />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="hd-why">Why does it matter? <span className="text-muted-foreground">(optional)</span></Label>
            <Textarea id="hd-why" value={form.why} onChange={set("why")} rows={2}
              placeholder="e.g. I have a client proposal going out Friday" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Type</Label>
              <Select value={form.category} onValueChange={set("category")}>
                <SelectTrigger data-testid="hd-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(CATEGORY).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>How soon?</Label>
              <Select value={form.urgency} onValueChange={set("urgency")}>
                <SelectTrigger data-testid="hd-urgency"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(URGENCY).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Part of HQ <span className="text-muted-foreground">(optional)</span></Label>
            <Select value={form.area || "none"} onValueChange={(v) => setForm((f) => ({ ...f, area: v === "none" ? "" : v }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Not sure</SelectItem>
                {AREAS.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Screenshots or files <span className="text-muted-foreground">(optional)</span></Label>
              <label className="inline-flex items-center gap-1.5 text-[13px] text-estate-700 cursor-pointer">
                <Paperclip className="w-3.5 h-3.5" /> Attach
                <input type="file" multiple className="hidden" onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
                  accept="image/*,.pdf,.txt,.csv,.xlsx,.docx,.mov,.mp4" data-testid="hd-file" />
              </label>
            </div>
            {files.length === 0 ? (
              <p className="text-[12px] text-muted-foreground">Tip: paste a screenshot here with ⌘V.</p>
            ) : (
              <ul className="space-y-1">
                {files.map((f, i) => (
                  <li key={i} className="flex items-center justify-between text-[13px] border-b border-border/60 py-1">
                    <span className="truncate">{f.name}</span>
                    <button type="button" className="text-muted-foreground hover:text-foreground"
                      onClick={() => setFiles((prev) => prev.filter((_, j) => j !== i))} aria-label={`Remove ${f.name}`}>
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {pageUrl && (
            <p className="text-[12px] text-muted-foreground">Filed from <span className="font-mono">{pageUrl}</span></p>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="hd-submit">
            {saving ? "Filing…" : "File request"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
